export enum PerkCategories {
  STANDARD = 'Standard',
  ULTIMATE = 'Ultimate',
  TRADEOFF = 'Tradeoff',
}

export enum Scaling {
  NONE = 'None',
  MULTIPLICATIVE = 'Multiplicative',
  ADDITIVE = 'Additive',
}

export type Perk = {
  category: PerkCategories;
  maxLevel: number;
  text: string;
  value: number;
  scaling: Scaling;
  debuff: number;
};

export enum StandardPerk {
  MAX_HEALTH = 'MAX_HEALTH',
  DAMAGE = 'DAMAGE',
  HEALTH_REGEN = 'HEALTH_REGEN',
  COIN_BONUSES = 'COIN_BONUSES',
  BOUNCE_SHOT = 'BOUNCE_SHOT',
  INTEREST = 'INTEREST',
  LAND_MINE_DAMAGE = 'LAND_MINE_DAMAGE',
  ORBS = 'ORBS',
  FREE_UPGRADE_CHANCE = 'FREE_UPGRADE_CHANCE',
  DEFENSE_PERCENT = 'DEFENSE_PERCENT',
  PERK_WAVE_REQUIREMENT = 'PERK_WAVE_REQUIREMENT',
  UNLOCK_ULTIMATE_WEAPON = 'UNLOCK_ULTIMATE_WEAPON',
  INCREASE_GAME_SPEED = 'INCREASE_GAME_SPEED',
}

export enum UltimatePerk {
  SMART_MISSILES = 'SMART_MISSILES',
  POISON_SWAMP = 'POISON_SWAMP',
  DEATH_WAVE = 'DEATH_WAVE',
  GOLDEN_TOWER = 'GOLDEN_TOWER',
  CHAIN_LIGHTNING = 'CHAIN_LIGHTNING',
  CHRONO_FIELD = 'CHRONO_FIELD',
  INNER_LAND_MINES = 'INNER_LAND_MINES',
}

export enum UltimateWeapons {
  SMART_MISSILES = 'SMART_MISSILES',
  POISON_SWAMP = 'POISON_SWAMP',
  DEATH_WAVE = 'DEATH_WAVE',
  GOLDEN_TOWER = 'GOLDEN_TOWER',
  CHAIN_LIGHTNING = 'CHAIN_LIGHTNING',
  CHRONO_FIELD = 'CHRONO_FIELD',
  INNER_LAND_MINES = 'INNER_LAND_MINES',
  BLACK_HOLE = 'BLACK_HOLE',
  SPOTLIGHT = 'SPOTLIGHT',
}

export enum TradeoffPerk {
  DAMAGE_BOSS_HEALTH = 'DAMAGE_BOSS_HEALTH',
  COINS_HEALTH = 'COINS_HEALTH',
  HEALTH_REGEN_LIFESTEAL = 'HEALTH_REGEN_LIFESTEAL',
  DAMAGE_DAMAGE = 'DAMAGE_DAMAGE',
  RANGE_RANGE = 'RANGE_RANGE',
  SPEED_DAMAGE = 'SPEED_DAMAGE',
  CASH_WAVE_KILL = 'CASH_WAVE_KILL',
  REGEN_HEALTH = 'REGEN_HEALTH',
  BOSS_HEALTH_SPEED = 'BOSS_HEALTH_SPEED',
  LIFESTEAL_KNOCKBACK = 'LIFESTEAL_KNOCKBACK',
}

export type PerkTypes = StandardPerk | UltimatePerk | TradeoffPerk;

export const StandardPerks: { [key in StandardPerk]: Perk } = {
  [StandardPerk.MAX_HEALTH]: {
    category: PerkCategories.STANDARD,
    maxLevel: 5,
    text: 'max Health x{{value}}',
    value: 1.2,
    debuff: 0,
    scaling: Scaling.MULTIPLICATIVE,
  },
  [StandardPerk.DAMAGE]: {
    category: PerkCategories.STANDARD,
    maxLevel: 5,
    text: 'Damage x{{value}}',
    value: 1.15,
    debuff: 0,
    scaling: Scaling.MULTIPLICATIVE,
  },
  [StandardPerk.HEALTH_REGEN]: {
    category: PerkCategories.STANDARD,
    maxLevel: 5,
    text: 'Health Regen x{{value}}',
    value: 1.6,
    debuff: 0,
    scaling: Scaling.MULTIPLICATIVE,
  },
  [StandardPerk.COIN_BONUSES]: {
    category: PerkCategories.STANDARD,
    maxLevel: 5,
    text: 'Coin Bonuses x{{value}}',
    value: 1.15,
    debuff: 0,
    scaling: Scaling.MULTIPLICATIVE,
  },
  [StandardPerk.BOUNCE_SHOT]: {
    category: PerkCategories.STANDARD,
    maxLevel: 3,
    text: 'Bounce Shot +{{value}}',
    value: 2,
    debuff: 0,
    scaling: Scaling.NONE,
  },
  [StandardPerk.INTEREST]: {
    category: PerkCategories.STANDARD,
    maxLevel: 5,
    text: 'Interest x{{value}}',
    value: 1.5,
    debuff: 0,
    scaling: Scaling.MULTIPLICATIVE,
  },
  [StandardPerk.LAND_MINE_DAMAGE]: {
    category: PerkCategories.STANDARD,
    maxLevel: 5,
    text: 'Land Mine Damage x{{value}}',
    value: 3.5,
    debuff: 0,
    scaling: Scaling.MULTIPLICATIVE,
  },
  [StandardPerk.ORBS]: {
    category: PerkCategories.STANDARD,
    maxLevel: 2,
    text: 'Orbs +{{value}}',
    value: 1,
    debuff: 0,
    scaling: Scaling.NONE,
  },
  [StandardPerk.FREE_UPGRADE_CHANCE]: {
    category: PerkCategories.STANDARD,
    maxLevel: 5,
    text: 'Free upgrade chance for all +{{value}}%',
    value: 5,
    debuff: 0,
    scaling: Scaling.ADDITIVE,
  },
  [StandardPerk.DEFENSE_PERCENT]: {
    category: PerkCategories.STANDARD,
    maxLevel: 5,
    text: 'Defence percent +{{value}}%',
    value: 4,
    debuff: 0,
    scaling: Scaling.ADDITIVE,
  },
  [StandardPerk.PERK_WAVE_REQUIREMENT]: {
    category: PerkCategories.STANDARD,
    maxLevel: 3,
    text: 'Perk wave requirement -{{value}}%',
    value: 20,
    debuff: 0,
    scaling: Scaling.ADDITIVE,
  },
  [StandardPerk.UNLOCK_ULTIMATE_WEAPON]: {
    category: PerkCategories.STANDARD,
    maxLevel: 1,
    text: 'Unlock a random ultimate weapon',
    value: 1,
    debuff: 0,
    scaling: Scaling.NONE,
  },
  [StandardPerk.INCREASE_GAME_SPEED]: {
    category: PerkCategories.STANDARD,
    maxLevel: 1,
    text: 'Increase game speed by {{value}}',
    value: 1,
    debuff: 0,
    scaling: Scaling.NONE,
  },
};

export const UltimatePerks: { [key in UltimatePerk]: Perk } = {
  [UltimatePerk.SMART_MISSILES]: {
    category: PerkCategories.ULTIMATE,
    maxLevel: 1,
    text: '{{value}} extra Smart missiles',
    value: 4,
    debuff: 0,
    scaling: Scaling.NONE,
  },
  [UltimatePerk.POISON_SWAMP]: {
    category: PerkCategories.ULTIMATE,
    maxLevel: 1,
    text: 'Swamp radius x{{value}}',
    value: 1.5,
    debuff: 0,
    scaling: Scaling.MULTIPLICATIVE,
  },
  [UltimatePerk.DEATH_WAVE]: {
    category: PerkCategories.ULTIMATE,
    maxLevel: 1,
    text: '+{{value}} wave on death wave',
    value: 1,
    debuff: 0,
    scaling: Scaling.NONE,
  },
  [UltimatePerk.GOLDEN_TOWER]: {
    category: PerkCategories.ULTIMATE,
    maxLevel: 1,
    text: 'Golden tower bonus x{{value}}',
    value: 1.5,
    debuff: 0,
    scaling: Scaling.MULTIPLICATIVE,
  },
  [UltimatePerk.CHAIN_LIGHTNING]: {
    category: PerkCategories.ULTIMATE,
    maxLevel: 1,
    text: 'Chain lightning damage x{{value}}',
    value: 2,
    debuff: 0,
    scaling: Scaling.MULTIPLICATIVE,
  },
  [UltimatePerk.CHRONO_FIELD]: {
    category: PerkCategories.ULTIMATE,
    maxLevel: 1,
    text: 'Chrono field radius x{{value}}',
    value: 1.5,
    debuff: 0,
    scaling: Scaling.MULTIPLICATIVE,
  },
  [UltimatePerk.INNER_LAND_MINES]: {
    category: PerkCategories.ULTIMATE,
    maxLevel: 1,
    text: 'Extra set of inner mines',
    value: 1,
    debuff: 0,
    scaling: Scaling.NONE,
  },
};

export const TradeoffPerks: { [key in TradeoffPerk]: Perk } = {
  [TradeoffPerk.DAMAGE_BOSS_HEALTH]: {
    category: PerkCategories.TRADEOFF,
    maxLevel: 1,
    text: 'x{{value}} tower damage, but bosses have x{{debuff}} health',
    value: 1.5,
    debuff: 8,
    scaling: Scaling.MULTIPLICATIVE,
  },
  [TradeoffPerk.COINS_HEALTH]: {
    category: PerkCategories.TRADEOFF,
    maxLevel: 1,
    text: 'x{{value}} coins, but tower max health -{{debuff}}%',
    value: 1.8,
    debuff: 70,
    scaling: Scaling.MULTIPLICATIVE,
  },
  [TradeoffPerk.HEALTH_REGEN_LIFESTEAL]: {
    category: PerkCategories.TRADEOFF,
    maxLevel: 1,
    text: 'Enemies have -{{value}}% health, but tower health regen and lifesteal -{{debuff}}%',
    value: 50,
    debuff: 90,
    scaling: Scaling.ADDITIVE,
  },
  [TradeoffPerk.DAMAGE_DAMAGE]: {
    category: PerkCategories.TRADEOFF,
    maxLevel: 1,
    text: 'Enemies damage -{{value}}%, but tower damage -{{debuff}}%',
    value: 50,
    debuff: 50,
    scaling: Scaling.ADDITIVE,
  },
  [TradeoffPerk.RANGE_RANGE]: {
    category: PerkCategories.TRADEOFF,
    maxLevel: 1,
    text: 'Ranged enemies attack distance reduced, but ranged enemies damage x{{debuff}}',
    value: 0.3,
    debuff: 3,
    scaling: Scaling.MULTIPLICATIVE,
  },
  [TradeoffPerk.SPEED_DAMAGE]: {
    category: PerkCategories.TRADEOFF,
    maxLevel: 1,
    text: 'Enemies speed -{{value}}%, but enemies damage x{{debuff}}',
    value: 50,
    debuff: 2.5,
    scaling: Scaling.ADDITIVE,
  },
  [TradeoffPerk.CASH_WAVE_KILL]: {
    category: PerkCategories.TRADEOFF,
    maxLevel: 1,
    text: "x{{value}} cash per wave, but enemy kills don't give cash",
    value: 12,
    debuff: 100,
    scaling: Scaling.MULTIPLICATIVE,
  },
  [TradeoffPerk.REGEN_HEALTH]: {
    category: PerkCategories.TRADEOFF,
    maxLevel: 1,
    text: 'tower health regen x{{value}}, but tower max health -{{debuff}}%',
    value: 8,
    debuff: 60,
    scaling: Scaling.MULTIPLICATIVE,
  },
  [TradeoffPerk.BOSS_HEALTH_SPEED]: {
    category: PerkCategories.TRADEOFF,
    maxLevel: 1,
    text: 'Boss health -{{value}}%, but boss speed +{{debuff}}%',
    value: 70,
    debuff: 50,
    scaling: Scaling.ADDITIVE,
  },
  [TradeoffPerk.LIFESTEAL_KNOCKBACK]: {
    category: PerkCategories.TRADEOFF,
    maxLevel: 1,
    text: 'Lifesteal x{{value}}, but knockback force -{{debuff}}%',
    value: 2.5,
    debuff: 70,
    scaling: Scaling.MULTIPLICATIVE,
  },
};

export const Perks: {
  [key in StandardPerk | UltimatePerk | TradeoffPerk]: Perk;
} = {
  ...StandardPerks,
  ...UltimatePerks,
  ...TradeoffPerks,
};
